// Data Validation Service - Verify real-time data quality
// Compares our data with Yahoo Finance to ensure accuracy

export interface DataValidationResult {
  symbol: string;
  isRealTime: boolean;
  dataAge: number; // in minutes
  priceMatch: boolean;
  marketState: string;
  lastUpdate: string;
  warnings: string[];
}

export interface ValidationReport {
  timestamp: string;
  totalSymbols: number;
  realTimeCount: number;
  delayedCount: number;
  errorCount: number;
  results: DataValidationResult[];
  overallHealth: 'EXCELLENT' | 'GOOD' | 'WARNING' | 'CRITICAL';
}

class DataValidationService {
  private validationHistory: ValidationReport[] = [];
  private readonly MAX_HISTORY = 50;

  // Validate single symbol data quality
  async validateSymbolData(symbol: string, price: number, timestamp: number): Promise<DataValidationResult> {
    const warnings: string[] = [];
    const now = Date.now();
    const dataAge = Math.round((now - timestamp) / 60000); // minutes
    
    // Check data freshness
    let isRealTime = true;
    if (dataAge > 5) {
      isRealTime = false;
      warnings.push(`Data is ${dataAge} minutes old`);
    }
    
    // Check price validity
    let priceMatch = true;
    if (price <= 0 || !isFinite(price)) {
      priceMatch = false;
      warnings.push('Invalid price data');
    }
    
    // Determine market state based on symbol and time
    const marketState = this.getExpectedMarketState(symbol);
    
    return {
      symbol,
      isRealTime,
      dataAge,
      priceMatch,
      marketState,
      lastUpdate: new Date(timestamp).toISOString(),
      warnings
    };
  }

  // Get expected market state for symbol
  private getExpectedMarketState(symbol: string): string {
    const now = new Date();
    const hour = now.getUTCHours();
    const day = now.getUTCDay(); // 0 = Sunday, 6 = Saturday
    
    // Forex markets (24/5)
    if (['EUR', 'GBP', 'CHF', 'AUD', 'NZD', 'CAD', 'JPY', 'DXY'].includes(symbol)) {
      // Forex closed from Friday 22:00 UTC to Sunday 22:00 UTC
      if (day === 6 || (day === 0 && hour < 22) || (day === 5 && hour >= 22)) {
        return 'CLOSED';
      }
      return 'OPEN';
    }
    
    // US Stock markets (Monday-Friday, 14:30-21:00 UTC)
    if (['SP500', 'NASDAQ', 'DOW'].includes(symbol)) {
      if (day === 0 || day === 6) return 'CLOSED'; // Weekend
      if (hour < 14 || hour >= 21) return 'CLOSED'; // Outside trading hours
      return 'OPEN';
    }
    
    // Commodities (various hours, assume 24/5 for simplicity)
    if (['GOLD', 'SILVER', 'COPPER', 'PLATINUM', 'CRUDE', 'GASOLINE', 'NATGAS'].includes(symbol)) {
      if (day === 6 || day === 0) return 'CLOSED'; // Weekend
      return 'OPEN';
    }
    
    return 'UNKNOWN';
  }

  // Validate all symbols and generate report
  async generateValidationReport(symbolsData: Array<{symbol: string, price: number, timestamp: number}>): Promise<ValidationReport> {
    const results: DataValidationResult[] = [];
    let realTimeCount = 0;
    let delayedCount = 0;
    let errorCount = 0;
    
    for (const data of symbolsData) {
      try {
        const result = await this.validateSymbolData(data.symbol, data.price, data.timestamp);
        results.push(result);
        
        if (result.isRealTime && result.priceMatch) {
          realTimeCount++;
        } else if (!result.isRealTime && result.priceMatch) {
          delayedCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
        results.push({
          symbol: data.symbol,
          isRealTime: false,
          dataAge: 999,
          priceMatch: false,
          marketState: 'ERROR',
          lastUpdate: new Date().toISOString(),
          warnings: [`Validation error: ${error}`]
        });
      }
    }
    
    // Determine overall health
    const totalSymbols = symbolsData.length;
    const healthPercentage = (realTimeCount / totalSymbols) * 100;
    
    let overallHealth: 'EXCELLENT' | 'GOOD' | 'WARNING' | 'CRITICAL';
    if (healthPercentage >= 90) overallHealth = 'EXCELLENT';
    else if (healthPercentage >= 75) overallHealth = 'GOOD';
    else if (healthPercentage >= 50) overallHealth = 'WARNING';
    else overallHealth = 'CRITICAL';
    
    const report: ValidationReport = {
      timestamp: new Date().toISOString(),
      totalSymbols,
      realTimeCount,
      delayedCount,
      errorCount,
      results,
      overallHealth
    };
    
    // Store in history
    this.validationHistory.push(report);
    if (this.validationHistory.length > this.MAX_HISTORY) {
      this.validationHistory.shift();
    }
    
    // Log report summary
    console.log(`📊 DATA VALIDATION REPORT - ${overallHealth}`);
    console.log(`✅ Real-time: ${realTimeCount}/${totalSymbols} (${Math.round(healthPercentage)}%)`);
    console.log(`⏰ Delayed: ${delayedCount}, ❌ Errors: ${errorCount}`);
    
    if (overallHealth === 'WARNING' || overallHealth === 'CRITICAL') {
      console.warn('⚠️ DATA QUALITY ISSUES DETECTED:');
      results.filter(r => r.warnings.length > 0).forEach(r => {
        console.warn(`  ${r.symbol}: ${r.warnings.join(', ')}`);
      });
    }
    
    return report;
  }

  // Get latest validation report
  getLatestReport(): ValidationReport | null {
    return this.validationHistory[this.validationHistory.length - 1] || null;
  }

  // Get validation history
  getValidationHistory(): ValidationReport[] {
    return [...this.validationHistory];
  }

  // Check if data quality is acceptable
  isDataQualityAcceptable(): boolean {
    const latest = this.getLatestReport();
    if (!latest) return false;
    
    return latest.overallHealth === 'EXCELLENT' || latest.overallHealth === 'GOOD';
  }

  // Get symbols with issues
  getProblematicSymbols(): string[] {
    const latest = this.getLatestReport();
    if (!latest) return [];
    
    return latest.results
      .filter(r => !r.isRealTime || !r.priceMatch)
      .map(r => r.symbol);
  }

  // Clear validation history
  clearHistory(): void {
    this.validationHistory = [];
    console.log('🗑️ Validation history cleared');
  }
}

export const dataValidationService = new DataValidationService();
export default dataValidationService;